import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {ProtelConfig} from "crqlar-react-common/dist/models/integrations/protel/protelConfig.model";
import {ProtelService} from "crqlar-react-common/dist/services/integrations/protel/protel.service";
import {Box, Divider, Stack, Tab, Tabs} from "@mui/material";
import Typography from "@mui/material/Typography";
import Logs from "./Logs";
import ProtelConfigForm from "./ProtelConfigForm";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";

const ProtelConfigDetails = () => {

    useTrackScreenVisit('system protel config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const protelConfigData = useEntity<ProtelConfig>({
        get: async () => {
            return await ProtelService.find()
        },
        create: ProtelService.create,
        update: ProtelService.update,
        delete: (id) => ProtelService.delete({id: [id]}),
        isValid: (protelConfig) => {
            return !!protelConfig.name
        },
        cleanupBeforeSave: (protelConfig) => {
            return {
                ...protelConfig,
                name: protelConfig.name?.trim()
            }
        }
    })

    const protelConfig = protelConfigData.item

    const handleCreate = () => {
        protelConfigData.startCreate({
            enabled: true,
            processingEnabled: true,
            otaMappingRules: {
                autoCreateResourceIfNotFound: true,
                pickAdditionalResourceInfo: true,
                autoCreateServiceForRatePlanIfNotFound: true
            }
        } as ProtelConfig)
    }

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={protelConfig.name}
            data={protelConfigData}
            back={() => navigate('/system')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={protelConfigData.saving}
                onDelete={() => protelConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {protelConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    protelConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!protelConfig.id &&
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        aria-label="tabs"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Tab label={t('Configuration')} value={'config'}/>
                        <Tab label={t('Stats and Logs')} value={'logs'}/>
                    </Tabs>
                }

                {
                    !protelConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <ProtelConfigForm protelConfigData={protelConfigData}/>

                            {
                                !!protelConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'logs' && (
                        <Logs protelConfigData={protelConfigData}/>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {protelConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    );
}

export default ProtelConfigDetails;
